import { z } from "zod";

/** One user's guided-tour progress, stored per vendor in `VendorProperties.tourCompletions`. */
export const tourCompletionSchema = z
  .object({
    userEmail: z.email(),
    /** Main-tour registry version last completed or skipped; absent until the user has done so */
    version: z.number().int().positive().optional(),
    /** Highest step count reached in the user's most recent full-tour run */
    stepsCompleted: z.number().int().nonnegative().optional(),
    /** True once the onboarding setup guide was skipped or its map guide finished */
    onboardingTourCompleted: z.boolean().optional(),
  })
  .refine((entry) => entry.version !== undefined || entry.stepsCompleted !== undefined || entry.onboardingTourCompleted === true, {
    message: "A completion entry must record a version, stepsCompleted or onboardingTourCompleted: true",
  });

export type TourCompletion = z.infer<typeof tourCompletionSchema>;

/** An entry minus its identity: what the server merges and the client sends as changes */
export type TourCompletionUpdate = Omit<TourCompletion, "userEmail">;

/** Request shape for the vendor-preferences write: exactly the caller's own entry */
export const tourCompletionRequestSchema = z.tuple([tourCompletionSchema]);

/** Emails are compared and stored in this form so lookups are case-insensitive */
export const normalizeTourEmail = (email: string): string => email.trim().toLowerCase();

export const findTourCompletion = (entries: readonly TourCompletion[], email: string): TourCompletion | undefined => {
  const normalizedEmail = normalizeTourEmail(email);
  return entries.find((entry) => normalizeTourEmail(entry.userEmail) === normalizedEmail);
};

/** Newer version replaces stepsCompleted; same version keeps the higher count; older/missing writes keep the existing value. */
const mergeStepsCompleted = (existing: TourCompletion | undefined, incoming: TourCompletionUpdate): number | undefined => {
  if (incoming.stepsCompleted === undefined) return existing?.stepsCompleted;
  const existingVersion = existing?.version ?? 0;
  const incomingVersion = incoming.version ?? 0;
  if (existing === undefined || incomingVersion > existingVersion) return incoming.stepsCompleted;
  if (incomingVersion === existingVersion) return Math.max(existing.stepsCompleted ?? 0, incoming.stepsCompleted);
  return existing.stepsCompleted;
};

/** Highest version either side has; undefined while the user has never finished or skipped the main tour */
const mergeVersion = (existing: TourCompletion | undefined, incoming: TourCompletionUpdate): number | undefined => {
  if (existing?.version === undefined) return incoming.version;
  if (incoming.version === undefined) return existing.version;
  return Math.max(existing.version, incoming.version);
};

/** Merges a completion write into the per-user list; version never downgrades and the onboarding flag never clears. */
export const upsertTourCompletion = (
  entries: readonly TourCompletion[],
  email: string,
  incoming: TourCompletionUpdate
): TourCompletion[] => {
  const normalizedEmail = normalizeTourEmail(email);
  const existing = findTourCompletion(entries, normalizedEmail);
  const otherEntries = entries.filter((entry) => normalizeTourEmail(entry.userEmail) !== normalizedEmail);

  const version = mergeVersion(existing, incoming);
  const stepsCompleted = mergeStepsCompleted(existing, incoming);
  const onboardingTourCompleted = existing?.onboardingTourCompleted === true || incoming.onboardingTourCompleted === true;

  // `existing` first, so fields this code does not know about survive the write
  const merged: TourCompletion = {
    ...existing,
    userEmail: normalizedEmail,
    ...(version === undefined ? {} : { version }),
    ...(stepsCompleted === undefined ? {} : { stepsCompleted }),
    ...(onboardingTourCompleted ? { onboardingTourCompleted } : {}),
  };
  return [...otherEntries, merged];
};
